import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, X, ArrowLeftRight, Image as ImageIcon, Calendar, ChevronLeft, ChevronRight, GitCompare,
  Stethoscope, Activity, Flame, FlaskConical, Syringe, Sparkles, Info,
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  appointment: { label: 'Appointment', short: 'Appt', icon: Stethoscope, color: 'bg-blue-500', soft: 'bg-blue-100 text-blue-700' },
  symptom: { label: 'Symptom', short: 'Sym', icon: Activity, color: 'bg-yellow-500', soft: 'bg-yellow-100 text-yellow-700' },
  flare: { label: 'Flare', short: 'Flare', icon: Flame, color: 'bg-orange-500', soft: 'bg-orange-100 text-orange-700' },
  lab: { label: 'Lab', short: 'Lab', icon: FlaskConical, color: 'bg-cyan-500', soft: 'bg-cyan-100 text-cyan-700' },
  vaccine: { label: 'Vaccine', short: 'Vax', icon: Syringe, color: 'bg-teal-500', soft: 'bg-teal-100 text-teal-700' },
};

/**
 * PhotoCompareDialog
 * Pulls photos and lets the user pick TWO to compare side-by-side. Photos are sorted oldest→newest.
 *
 * Props:
 *   open, onOpenChange — controlled state
 *   token — auth token
 *   parentType — 'symptom' | 'flare' | 'lab' | 'appointment' | 'vaccine' | null (null = all types, cross-category compare)
 *   title — dialog title (optional)
 *   description — dialog description (optional)
 *   filterOptions — array of strings used to pre-filter by record_label (e.g. child names, lab test names)
 *   filterLabel — label for the "All" pill (defaults to 'All')
 *   crossType — when true and parentType is null, also shows type filter pills (Appointment / Symptom / Flare / Lab / Vaccine)
 */
const PhotoCompareDialog = ({
  open,
  onOpenChange,
  token,
  parentType = 'symptom',
  title,
  description,
  filterOptions = [],
  filterLabel = 'All',
  crossType = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [blobs, setBlobs] = useState({});
  const [filterValue, setFilterValue] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [leftIdx, setLeftIdx] = useState(0);
  const [rightIdx, setRightIdx] = useState(1);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiObservation, setAiObservation] = useState(null); // {observation, disclaimer}

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const url = parentType
        ? `${API}/api/attachments/all?parent_type=${parentType}`
        : `${API}/api/attachments/all`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = (res.data.attachments || []).slice().sort((a, b) => {
        const da = a.record_date || a.created_at;
        const db = b.record_date || b.created_at;
        return new Date(da) - new Date(db);
      });
      setPhotos(list);
      setLeftIdx(0);
      setRightIdx(Math.max(0, list.length - 1));
    } catch (e) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [token, parentType]);

  useEffect(() => {
    if (open) fetchPhotos();
  }, [open, fetchPhotos]);

  // Lazy-load all blobs so the strip + side-by-side can render quickly
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = { ...blobs };
      for (const p of photos) {
        if (next[p.id]) continue;
        try {
          const r = await fetch(`${API}/api/attachments/${p.id}/download?auth=${encodeURIComponent(token)}`);
          if (!r.ok) continue;
          const b = await r.blob();
          if (cancelled) return;
          next[p.id] = URL.createObjectURL(b);
        } catch (e) { /* skip */ }
      }
      if (!cancelled) setBlobs(next);
    };
    if (photos.length > 0) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(() => () => {
    Object.values(blobs).forEach(URL.revokeObjectURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPhotos = useMemo(() => {
    let result = photos;
    if (crossType && typeFilter !== 'all') {
      result = result.filter(p => p.parent_type === typeFilter);
    }
    if (filterValue !== 'all') {
      result = result.filter(p => (p.record_label || '').toLowerCase() === filterValue.toLowerCase());
    }
    return result;
  }, [photos, filterValue, typeFilter, crossType]);

  // Counts per type for the cross-type pills
  const typeCounts = useMemo(() => {
    const c = { all: photos.length };
    photos.forEach(p => { c[p.parent_type] = (c[p.parent_type] || 0) + 1; });
    return c;
  }, [photos]);

  // Reset indices when filter changes
  useEffect(() => {
    setLeftIdx(0);
    setRightIdx(Math.max(0, filteredPhotos.length - 1));
  }, [filteredPhotos.length]);

  // Clear AI observation when the pair changes
  useEffect(() => {
    setAiObservation(null);
  }, [leftIdx, rightIdx, filteredPhotos.length]);

  const left = filteredPhotos[leftIdx];
  const right = filteredPhotos[rightIdx];

  const runAiCompare = async () => {
    if (!left || !right || left.id === right.id) {
      toast.error('Pick two different photos to compare');
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/attachments/compare`,
        { left_attachment_id: left.id, right_attachment_id: right.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiObservation(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI comparison failed');
    } finally {
      setAiLoading(false);
    }
  };

  const fmtDate = (s) => s ? formatLocalDate(s, undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const daysBetween = useMemo(() => {
    if (!left || !right) return null;
    const a = new Date(left.record_date || left.created_at);
    const b = new Date(right.record_date || right.created_at);
    return Math.abs(Math.round((b - a) / (1000 * 60 * 60 * 24)));
  }, [left, right]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="photo-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-primary" /> {title || 'Compare Photos'}
          </DialogTitle>
          <DialogDescription>
            {description || 'Pick two photos from the strip below to see them side by side. Photos are sorted oldest → newest.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredPhotos.length < 2 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-[#57534E] font-medium">
              {filteredPhotos.length === 0
                ? 'No photos yet.'
                : 'You need at least 2 photos to compare.'}
            </p>
            <p className="text-sm text-stone-500 mt-1">
              Attach photos to entries to track visual progression over time.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Cross-type pills */}
            {crossType && (
              <div className="flex flex-wrap gap-2" data-testid="cross-type-filter">
                {['all', 'appointment', 'symptom', 'flare', 'lab', 'vaccine'].map(t => {
                  const meta = t === 'all' ? null : TYPE_META[t];
                  const Icon = meta?.icon;
                  const active = typeFilter === t;
                  const n = typeCounts[t] || 0;
                  if (t !== 'all' && n === 0) return null;
                  return (
                    <button
                      key={t}
                      onClick={() => setTypeFilter(t)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 border-2 transition-all ${
                        active
                          ? (t === 'all' ? 'bg-primary text-white border-primary' : `${meta.soft} border-current`)
                          : 'bg-stone-100 text-stone-500 border-transparent hover:bg-stone-200'
                      }`}
                      data-testid={`compare-type-${t}`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {t === 'all' ? 'All Types' : meta.label}
                      <span className="opacity-70">({n})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Label filter pills (children, test names, etc.) */}
            {filterOptions.length > 1 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterValue('all')}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    filterValue === 'all' ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600'
                  }`}
                >
                  {filterLabel}
                </button>
                {filterOptions.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilterValue(c)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      filterValue === c ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {/* Side-by-side viewer */}
            <div className="grid grid-cols-2 gap-3">
              <PhotoPane
                photo={left}
                blob={blobs[left?.id]}
                fmtDate={fmtDate}
                onPrev={() => setLeftIdx(Math.max(0, leftIdx - 1))}
                onNext={() => setLeftIdx(Math.min(filteredPhotos.length - 1, leftIdx + 1))}
                disablePrev={leftIdx <= 0}
                disableNext={leftIdx >= filteredPhotos.length - 1}
                side="left"
                showTypeBadge={crossType}
              />
              <PhotoPane
                photo={right}
                blob={blobs[right?.id]}
                fmtDate={fmtDate}
                onPrev={() => setRightIdx(Math.max(0, rightIdx - 1))}
                onNext={() => setRightIdx(Math.min(filteredPhotos.length - 1, rightIdx + 1))}
                disablePrev={rightIdx <= 0}
                disableNext={rightIdx >= filteredPhotos.length - 1}
                side="right"
                showTypeBadge={crossType}
              />
            </div>

            {daysBetween !== null && (
              <div className="flex items-center justify-center gap-2 text-sm text-[#57534E] bg-stone-50 rounded-lg py-2">
                <ArrowLeftRight className="w-4 h-4" />
                <span className="font-semibold">{daysBetween}</span> day{daysBetween === 1 ? '' : 's'} apart
              </div>
            )}

            {/* AI compare CTA + result */}
            <div className="rounded-xl border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-4">
              {aiObservation ? (
                <div data-testid="ai-observation">
                  <div className="flex items-start gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-purple-900 mb-1">AI observation</p>
                      <p className="text-sm text-[#2D2D2D] leading-relaxed whitespace-pre-wrap">
                        {aiObservation.observation}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-3 pt-3 border-t border-purple-200">
                    <Info className="w-3.5 h-3.5 text-purple-700 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-purple-700 italic">{aiObservation.disclaimer}</p>
                  </div>
                  <button
                    type="button"
                    onClick={runAiCompare}
                    disabled={aiLoading}
                    className="text-xs text-purple-700 hover:underline mt-2"
                    data-testid="ai-compare-rerun"
                  >
                    Re-run observation
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-purple-900">What changed between these two?</p>
                      <p className="text-xs text-purple-700/80 mt-0.5">
                        Get a 2-3 sentence AI observation comparing both photos. Not medical advice.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={runAiCompare}
                    disabled={aiLoading || !left || !right || left?.id === right?.id}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="ai-compare-btn"
                  >
                    {aiLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> What changed?</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Timeline strip */}
            <div>
              <p className="text-xs font-semibold text-stone-500 mb-2 uppercase tracking-wider">
                Timeline ({filteredPhotos.length} photo{filteredPhotos.length === 1 ? '' : 's'})
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="compare-strip">
                {filteredPhotos.map((p, i) => {
                  const isLeft = i === leftIdx;
                  const isRight = i === rightIdx;
                  const typeMeta = TYPE_META[p.parent_type];
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        if (isLeft || isRight) return;
                        const distLeft = Math.abs(i - leftIdx);
                        const distRight = Math.abs(i - rightIdx);
                        if (distLeft <= distRight) setLeftIdx(i);
                        else setRightIdx(i);
                      }}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        isLeft ? 'border-blue-500 ring-2 ring-blue-200' :
                        isRight ? 'border-orange-500 ring-2 ring-orange-200' :
                        'border-stone-200 hover:border-stone-400'
                      }`}
                      data-testid={`strip-photo-${p.id}`}
                    >
                      {blobs[p.id] ? (
                        <img src={blobs[p.id]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                          <ImageIcon className="w-4 h-4 text-stone-400" />
                        </div>
                      )}
                      {crossType && typeMeta && (
                        <span
                          className={`absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full ${typeMeta.color} ring-2 ring-white`}
                          title={typeMeta.label}
                        />
                      )}
                      {isLeft && (
                        <span className="absolute top-0.5 left-0.5 bg-blue-500 text-white text-[8px] font-bold px-1 rounded">L</span>
                      )}
                      {isRight && !crossType && (
                        <span className="absolute top-0.5 right-0.5 bg-orange-500 text-white text-[8px] font-bold px-1 rounded">R</span>
                      )}
                      {isRight && crossType && (
                        <span className="absolute top-5 right-0.5 bg-orange-500 text-white text-[8px] font-bold px-1 rounded">R</span>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center">
                        {formatLocalDate(p.record_date || p.created_at, undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PhotoPane = ({ photo, blob, fmtDate, onPrev, onNext, disablePrev, disableNext, side, showTypeBadge }) => {
  if (!photo) return <div className="aspect-square bg-stone-100 rounded-lg" />;
  const badgeColor = side === 'left' ? 'bg-blue-500' : 'bg-orange-500';
  const typeMeta = TYPE_META[photo.parent_type];
  const TypeIcon = typeMeta?.icon;
  return (
    <div className="relative border-2 border-stone-200 rounded-lg overflow-hidden bg-stone-100" data-testid={`pane-${side}`}>
      <div className={`absolute top-2 left-2 z-10 ${badgeColor} text-white text-xs font-bold px-2 py-0.5 rounded`}>
        {side === 'left' ? 'BEFORE / EARLIER' : 'AFTER / LATER'}
      </div>
      <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
        {showTypeBadge && typeMeta && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded flex items-center gap-1 ${typeMeta.soft}`}>
            {TypeIcon && <TypeIcon className="w-2.5 h-2.5" />}
            {typeMeta.label}
          </span>
        )}
        <span className="bg-black/70 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {fmtDate(photo.record_date || photo.created_at)}
        </span>
      </div>
      <div className="aspect-square flex items-center justify-center">
        {blob ? (
          <img src={blob} alt="" className="w-full h-full object-contain" />
        ) : (
          <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-1 p-2 bg-gradient-to-t from-black/70 to-transparent">
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/10 h-8 px-2"
          onClick={onPrev}
          disabled={disablePrev}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-white text-xs font-medium truncate flex-1 text-center">
          {photo.record_label || photo.original_filename}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/10 h-8 px-2"
          onClick={onNext}
          disabled={disableNext}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default PhotoCompareDialog;
